/**
 * BudgetMinorItem Component
 *
 * 소분류 예산 항목을 표시하는 컴포넌트
 *
 * 책임:
 * - 소분류 카테고리 정보 표시 (아이콘, 이름)
 * - 예산 지정 여부에 따른 다른 UI 표시
 *   - 예산 지정: 진행도 바 + 금액 정보
 *   - 예산 미지정: "예산 미지정" 텍스트 + 사용 금액 (있으면)
 * - 대분류와 구분되는 스타일 (회색 배경, 들여쓰기)
 */

import type { MinorBudgetStatus } from '../types/Budget'
import type { Category } from '@/domains/category/types/Category'

/**
 * BudgetMinorItem Props
 */
export interface BudgetMinorItemProps {
  /** 소분류 예산 현황 */
  minorStatus: MinorBudgetStatus
  /** 소분류 카테고리 정보 (실제 구글 시트 데이터) */
  category: Category | undefined
}

/**
 * 소분류 예산 항목 컴포넌트
 *
 * 대분류 아래에 표시되며, 예산 유무에 따라 다른 스타일 적용
 */
export function BudgetMinorItem({ minorStatus, category }: BudgetMinorItemProps) {
  const { budget, spent, hasNoBudget } = minorStatus

  /**
   * 카테고리 정보
   * props로 받은 카테고리 사용 (실제 구글 시트 데이터)
   */
  const icon = category?.icon || '❓'
  const name = category?.name || '알 수 없음'

  /**
   * 숫자를 천단위 구분자로 포맷
   */
  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('ko-KR')
  }

  /**
   * 예산 미지정 상태
   *
   * 표시 내용:
   * - 카테고리 아이콘 + 이름
   * - "예산 미지정" 텍스트
   * - 지출이 있으면 사용 금액 표시
   */
  if (hasNoBudget) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 ml-8">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm text-gray-700 dark:text-gray-200">{name}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">예산 미지정</span>
        </div>
        {spent > 0 && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 ml-7">
            사용: {formatCurrency(spent)}원
          </div>
        )}
      </div>
    )
  }

  /**
   * 예산 지정 상태
   *
   * 표시 내용:
   * - 카테고리 아이콘 + 이름
   * - 사용 비율 표시
   * - 진행도 바 (색상 코딩)
   * - 금액 정보 (사용액 / 예산액)
   */
  const { remaining, percentage, isOverBudget } = minorStatus

  /**
   * 사용 비율에 따른 색상 결정
   */
  const getColor = (percentage: number, isOverBudget: boolean) => {
    if (isOverBudget) return 'red'
    if (percentage >= 90) return 'red'
    if (percentage >= 60) return 'orange'
    return 'green'
  }

  const color = getColor(percentage, isOverBudget)

  /**
   * Tailwind CSS 색상 클래스 매핑
   */
  const colorClasses = {
    green: {
      bg: 'bg-budget-safe',
      text: 'text-green-700 dark:text-green-300',
    },
    orange: {
      bg: 'bg-budget-warning',
      text: 'text-orange-700 dark:text-orange-300',
    },
    red: {
      bg: 'bg-budget-danger',
      text: 'text-red-700 dark:text-red-300',
    },
  }

  const currentColor = colorClasses[color]

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 ml-8">
      {/* Header: 아이콘, 이름, 비율 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{name}</span>
        </div>
        <span className={`text-sm font-bold ${currentColor.text}`}>
          {percentage.toFixed(0)}%
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-2 overflow-hidden">
        <div
          className={`${currentColor.bg} h-1.5 rounded-full transition-all duration-300 ease-out`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>

      {/* Amount Info */}
      <div className="text-xs text-gray-600 dark:text-gray-400">
        사용: {formatCurrency(spent)}원 / {formatCurrency(budget!.amount)}원
        {remaining < 0 && (
          <span className={`ml-2 ${currentColor.text} font-medium`}>
            (초과 {formatCurrency(Math.abs(remaining))}원)
          </span>
        )}
      </div>
    </div>
  )
}
